import { create } from 'zustand';
import { membersService, type Member, type MemberResponse } from 'src/services/members.service';
import { transformMembers, type MemberWithPlans } from 'src/lib/utils';
import { useInsuranceStore } from './insurance';
import { type InsurancePlan } from 'src/services/interfaces/insurance';

interface MembersState {
    items: Member[];
    transformedItems: MemberWithPlans[];
    current: Member | null;
    loading: boolean;
    error: string | null;
    pagination: {
        hasNextPage: boolean;
        total: number;
    };
    // filters / paging / sorting
    search: string;
    groupId: string | undefined;
    subscriptionStatus: string | undefined;
    offset: number;
    limit: number;
    column?: 'id' | 'firstName' | 'lastName' | 'createdAt' | 'updatedAt';
    order?: 'asc' | 'desc';

    setSearch: (v: string) => void;
    setGroupId: (v: string | undefined) => void;
    setSubscriptionStatus: (v: string | undefined) => void;
    setPage: (page: number) => void;
    setLimit: (limit: number) => void;
    setSort: (column: MembersState['column'], order: MembersState['order']) => void;

    loadList: (passedPlans?: InsurancePlan[]) => Promise<{ items: Member[]; transformedItems: MemberWithPlans[]; pagination: { hasNextPage: boolean; total: number; }; }>;
    loadOne: (id: string | number) => Promise<void>;
}

export const useMembersStore = create<MembersState>((set, get) => ({
    items: [],
    transformedItems: [],
    current: null,
    loading: false,
    error: null,
    pagination: {
        hasNextPage: false,
        total: 0,
    },
    search: '',
    groupId: undefined,
    subscriptionStatus: undefined,
    offset: 0,
    limit: 10,
    column: undefined,
    order: undefined,

    setSearch: (v) => set({ search: v, offset: 0 }),
    setGroupId: (v) => set({ groupId: v, offset: 0 }),
    setSubscriptionStatus: (v) => set({ subscriptionStatus: v, offset: 0 }),
    setPage: (page) => set({ offset: Math.max(0, (page - 1) * get().limit) }),
    setLimit: (limit) => set({ limit, offset: 0 }),
    setSort: (column, order) => set({ column, order, offset: 0 }),

    loadList: async (passedPlans?: InsurancePlan[]) => {
        set({ loading: true, error: null });
        try {
            const { search, groupId, subscriptionStatus, offset, limit, column, order } = get();

            // Filter out undefined, null and empty values
            const params: Record<string, any> = {};
            if (search && search.trim()) params.search = search.trim();
            if (groupId) params.groupId = groupId;
            if (subscriptionStatus) params.subscriptionStatus = subscriptionStatus;
            if (offset !== undefined && offset !== null) params.offset = offset;
            if (limit !== undefined && limit !== null) params.limit = limit;
            if (column !== undefined && column !== null) params.column = column;
            if (order !== undefined && order !== null) params.order = order;

            const plans = passedPlans || useInsuranceStore.getState().plans;
            if(!plans){
                return {
                    items: [],
                    transformedItems: [],
                    pagination: {
                        hasNextPage: false,
                        total: 0,
                    }
                };
            }

            const response: MemberResponse = await membersService.list(params);
            const transformedItems = transformMembers(response.data, plans);
            set({
                items: response.data,
                transformedItems,
                pagination: response.pagination
            });
            return {
                items: response.data,
                transformedItems,
                pagination: response.pagination
            }
        } catch (error: unknown) {
            set({ error: (error as { message?: string })?.message || 'Failed to load members' });
            throw error;
        } finally {
            set({ loading: false });
        }
    },

    loadOne: async (id) => {
        set({ loading: true, error: null });
        try {
            // Assuming there will be a getOne method in the future
            // For now, find from items
            const member = get().items.find((m) => m.id === id);
            if (member) {
                set({ current: member });
            } else {
                throw new Error('Member not found');
            }
        } catch (error: unknown) {
            set({ error: (error as { message?: string })?.message || 'Failed to load member' });
            throw error;
        } finally {
            set({ loading: false });
        }
    },
}));